import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { services } from '../mockData';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isServiceActive = location.pathname.startsWith('/services/') || location.pathname === '/delivery';

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img
              src="/logos/midtown-logo.png"
              alt="Midtown Earth Solutions LLC"
              className="h-14 sm:h-16 w-auto object-contain"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/about') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              About
            </Link>
            <Link
              to="/products"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/products') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              Products
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center ${
                    isServiceActive ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
                  }`}
                  data-testid="navbar-services-dropdown"
                >
                  Services
                  <ChevronDown className="ml-1 w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/calculator" className="cursor-pointer font-semibold text-green-800 flex items-center" data-testid="nav-calculator">
                    <Calculator className="w-4 h-4 mr-2" />
                    Material Calculator
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/delivery" className="cursor-pointer">
                    Delivery Services Overview
                  </Link>
                </DropdownMenuItem>
                {services.map(s => (
                  <DropdownMenuItem key={s.slug} asChild>
                    <Link to={`/services/${s.slug}`} className="cursor-pointer" data-testid={`nav-service-${s.slug}`}>
                      {s.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/gallery"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/gallery') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              Gallery
            </Link>
            <Link
              to="/service-areas"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/service-areas') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              Areas
            </Link>
            <Link
              to="/blog"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/blog') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/contact') ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Call Button */}
          <div className="hidden xl:flex items-center space-x-3">
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-green-800 text-green-800 hover:bg-green-800 hover:text-white" data-testid="navbar-request-quote">
                Request Quote
              </Button>
            </Link>
            <a href="tel:+17063663685" data-testid="navbar-call-now">
              <Button className="bg-green-800 hover:bg-green-900 text-white">
                <Phone className="w-4 h-4 mr-2" />
                (706) 366-3685
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="xl:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            data-testid="navbar-mobile-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="xl:hidden border-t border-gray-200 bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navLinks.slice(0, 3).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(link.path) ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile Services Section */}
            <div className="border-t border-gray-200 pt-2 mt-2">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-green-50 hover:text-green-800"
              >
                <span>Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 mt-1 space-y-1">
                  <Link
                    to="/calculator"
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-2 rounded-md text-sm font-semibold text-green-800 hover:bg-green-50"
                  >
                    Material Calculator
                  </Link>
                  <Link
                    to="/delivery"
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-green-50 hover:text-green-800"
                  >
                    Delivery Overview
                  </Link>
                  {services.map(s => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      onClick={() => setIsOpen(false)}
                      className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-green-50 hover:text-green-800"
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(3).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(link.path) ? 'bg-green-800 text-white' : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <a href="tel:+17063663685" className="block" onClick={() => setIsOpen(false)}>
              <Button className="w-full bg-green-800 hover:bg-green-900 text-white mt-2">
                <Phone className="w-4 h-4 mr-2" />
                Call (706) 366-3685
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
